<template>
    <component
        :is="embedded ? 'div' : 'main'"
        :class="embedded
            ? 'user-account-create-embedded flex-1 min-h-0 overflow-y-auto focus:outline-none'
            : 'flex-1 relative overflow-y-auto py-6 focus:outline-none'"
        tabindex="0"
    >
        <form novalidate @submit.prevent="saveUser">
            <div v-if="embedded" class="user-account-create-toolbar">
                <button
                    aria-label="Benutzeranlage schließen"
                    class="user-account-create-close"
                    title="Zurück"
                    type="button"
                    @click="$emit('close')"
                >
                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"/>
                </button>
                <span class="user-account-create-toolbar-title">Benutzerkonto anlegen</span>
                <button class="user-account-create-submit" type="submit">
                    Anlegen
                </button>
            </div>
            <div v-else class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">Benutzerkonto anlegen</h1>
                    </div>
                    <div class="inline-flex">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/users">
                            {{ $t('Cancel') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div :class="{'user-account-create-section': embedded}" class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User details') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Stammdaten und Kontaktdaten des Benutzerkontos.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="space-y-6">
                                <section>
                                    <h4 class="text-sm font-semibold text-gray-900">Persönliche Angaben</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-6">
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="salutation">{{ $t('Salutation') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <select
                                                    id="salutation"
                                                    v-model="user.salutation"
                                                    class="block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                >
                                                    <option :value="null">{{ $t('Select salutation') }}</option>
                                                    <option value="mr">{{ $t('Mr') }}</option>
                                                    <option value="ms">{{ $t('Ms') }}</option>
                                                    <option value="diverse">{{ $t('Diverse') }}</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div class="md:col-span-4">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="name"
                                                    v-model="user.name"
                                                    :placeholder="$t('Name')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    required
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Erreichbarkeit</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="email"
                                                    v-model="user.email"
                                                    :placeholder="$t('Email')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    required
                                                    type="email"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="phone_landline">{{ $t('Landline phone') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="phone_landline"
                                                    v-model="user.phone_landline"
                                                    :placeholder="$t('Landline phone')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="50"
                                                    pattern="\+?[0-9][0-9 ()/.\-]{2,49}"
                                                    title="Bitte geben Sie eine gültige Telefonnummer ein."
                                                    type="tel"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="phone_mobile">{{ $t('Mobile phone') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="phone_mobile"
                                                    v-model="user.phone_mobile"
                                                    :placeholder="$t('Mobile phone')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="50"
                                                    pattern="\+?[0-9][0-9 ()/.\-]{2,49}"
                                                    title="Bitte geben Sie eine gültige Telefonnummer ein."
                                                    type="tel"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Social Media</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="linkedin_url">LinkedIn</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="linkedin_url"
                                                    v-model.trim="user.linkedin_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.linkedin.com/in/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="xing_url">XING</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="xing_url"
                                                    v-model.trim="user.xing_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.xing.com/profile/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="instagram_url">Instagram</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="instagram_url"
                                                    v-model.trim="user.instagram_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.instagram.com/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="facebook_url">Facebook</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="facebook_url"
                                                    v-model.trim="user.facebook_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.facebook.com/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Anschrift</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-6">
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="street">{{ $t('Street') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="street"
                                                    v-model="user.street"
                                                    :placeholder="$t('Street')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-1">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="house_number">{{ $t('House number') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="house_number"
                                                    v-model="user.house_number"
                                                    placeholder="Nr"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="32"
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="postal_code">{{ $t('Postal code') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="postal_code"
                                                    v-model="user.postal_code"
                                                    :placeholder="$t('Postal code')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="20"
                                                    pattern="[A-Za-z0-9][A-Za-z0-9 \-]{1,19}"
                                                    title="Bitte geben Sie eine gültige Postleitzahl ein."
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="city">{{ $t('City') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="city"
                                                    v-model="user.city"
                                                    :placeholder="$t('City')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="120"
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="country">{{ $t('Country') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="country"
                                                    v-model="user.country"
                                                    :placeholder="$t('Country')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="120"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Zugangsdaten</h4>
                                    <div class="mt-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('Password') }}</label>
                                        <div class="mt-1 relative rounded shadow-sm">
                                            <input
                                                id="password"
                                                v-model="user.password"
                                                :placeholder="$t('Password')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                required
                                                type="password"
                                            >
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Rollenzuordnungen, Zugriffsrechte und verfügbare Module des Benutzerkontos.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="role">{{ $t('Role') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <select
                                            id="role"
                                            v-model="user.role_id"
                                            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                            <option :value="null" disabled>{{ $t('Select an option') }}</option>
                                            <option v-for="userRole in userRoles" :value="userRole.id">{{ userRole.name }}</option>
                                        </select>
                                    </div>
                                </div>
                                <user-company-assignment
                                    v-model:company-id="user.company_id"
                                    v-model:company-ids="user.company_ids"
                                    v-model:company-assignments="user.company_assignments"
                                    :companies="companies"
                                    :role-group="selectedRoleSlug"
                                />
                                <div v-if="isProjectAssignableRoleSelected" class="col-span-3">
                                    <h4 class="text-sm font-semibold text-gray-700">{{ $t('Projects') }}</h4>
                                    <div class="form-selection-panel mt-2 max-h-56 overflow-y-auto">
                                        <label
                                            v-for="project in projects"
                                            :key="'new-user-project-' + project.id"
                                            class="form-selection-option flex items-center justify-between px-3 py-2 cursor-pointer"
                                        >
                                            <div class="flex items-center min-w-0">
                                                <input
                                                    v-model="user.project_ids"
                                                    :value="project.id"
                                                    class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                    type="checkbox"
                                                >
                                                <img
                                                    :src="project.image_url ? project.image_url : '/images/default/icon.png'"
                                                    :alt="project.title"
                                                    class="h-8 w-8 rounded object-cover border border-gray-200 mr-3 flex-shrink-0"
                                                >
                                                <span class="text-sm text-gray-700 truncate">{{ project.title }}</span>
                                            </div>
                                        </label>
                                        <div v-if="projects.length < 1" class="px-3 py-3 text-sm text-gray-500">
                                            {{ $t('No projects available.') }}
                                        </div>
                                    </div>
                                </div>
                                <user-system-feature-settings
                                    v-if="showSystemFeatureSettings"
                                    :role-group="selectedRoleSlug"
                                    :value="user"
                                    class="col-span-3"
                                    @change="updateSystemFeature"
                                />
                                <div class="col-span-3">
                                    <h4 class="text-sm font-semibold text-gray-700">
                                        Kontakt im Bereich Beziehungen anzeigen
                                    </h4>
                                    <p class="mt-1 text-xs leading-5 text-gray-500">
                                        <template v-if="isCustomerRoleSelected">
                                            Angehörige des eigenen Unternehmens sind immer sichtbar. Für zusätzlich freigegebene Benutzergruppen erscheint der Account nur, wenn außerdem eine direkte Verknüpfung im System besteht, zum Beispiel über ein gemeinsames Unternehmen oder eine aktive Ticketteilnahme.
                                        </template>
                                        <template v-else>
                                            Die Checkbox erteilt nur die grundsätzliche Freigabe. Sichtbar wird der Account für die ausgewählte Benutzergruppe erst, wenn zusätzlich eine direkte Verknüpfung im System besteht, zum Beispiel über ein gemeinsames Unternehmen oder eine aktive Ticketteilnahme.
                                        </template>
                                    </p>
                                    <div class="form-selection-panel mt-2 overflow-hidden">
                                        <label v-if="!isCustomerRoleSelected" class="form-selection-option flex items-center px-3 py-2 cursor-pointer">
                                            <input
                                                v-model="user.relationships_contact_visible_to_customers"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Kunden</span>
                                        </label>
                                        <label class="form-selection-option flex items-center px-3 py-2 cursor-pointer">
                                            <input
                                                v-model="user.relationships_contact_visible_to_employees"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Mitarbeiter</span>
                                        </label>
                                        <label class="form-selection-option flex items-center px-3 py-2 cursor-pointer">
                                            <input
                                                v-model="user.relationships_contact_visible_to_service_providers"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Mitarbeitende Dienstleister</span>
                                        </label>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <input-switch
                                        id="status"
                                        v-model="user.status"
                                        :disabled-label="$t('The user is disabled')"
                                        :enabled-label="$t('The user is activated')"
                                    ></input-switch>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        Bei Deaktivierung bleibt das Benutzerkonto im System erhalten. Eine Anmeldung ist erst nach erneuter Aktivierung möglich.
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <div class="mt-1 flex rounded">
                                        <input-switch
                                            id="password_login_enabled"
                                            v-model="user.password_login_enabled"
                                            :disabled-label="$t('Activate password login')"
                                            :enabled-label="$t('Activate password login')"
                                        ></input-switch>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div :class="{'user-account-create-section': embedded}" class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('AI settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                KI-Funktionen sind nur verfügbar, wenn diese Einstellung und die globale KI-Einstellung aktiv sind.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="flex items-center">
                                <input-switch
                                    id="ai_enabled"
                                    v-model="user.ai_enabled"
                                ></input-switch>
                                <span class="ml-3 text-sm text-gray-700">{{ $t('Enable AI functions') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="showStorageLimitCard" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div :class="{'user-account-create-section': embedded}" class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Storage limit') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Speicherlimit im Dateimanager für dieses Benutzerkonto.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-1 gap-6">
                                <div>
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="file_manager_storage_limit_mb">
                                        {{ $t('Storage limit') }}
                                    </label>
                                    <select
                                        id="file_manager_storage_limit_mb"
                                        v-model="user.file_manager_storage_limit_mb"
                                        class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    >
                                        <option
                                            v-for="option in storageLimitOptions"
                                            :key="'storage-limit-option-new-' + String(option.id)"
                                            :value="option.id"
                                        >
                                            {{ option.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div :class="{'user-account-create-section': embedded}" class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Test account settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Testaccounts können über die Entwickleroptionen in Auswahllisten ausgeblendet werden.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="flex items-center">
                                <input-switch
                                    id="is_test_account"
                                    v-model="user.is_test_account"
                                ></input-switch>
                                <span class="ml-3 text-sm text-gray-700">{{ $t('mark as test account') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </component>
</template>

<script>
import UserCompanyAssignment from '@/components/users/user-company-assignment';
import UserSystemFeatureSettings from '@/components/users/user-system-feature-settings';
import {deriveRoleGroup} from "@/utils/access-context";
import {normalizeUserCompanyAssignment} from '@/utils/user-company-assignment';
import {isUserSystemFeatureVisible} from '@/utils/user-system-features';

export default {
    name: "new-user",
    components: {UserCompanyAssignment, UserSystemFeatureSettings},
    props: {
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    metaInfo() {
        return {
            title: 'Benutzerkonto anlegen'
        }
    },
    data() {
        return {
            loading: true,
            userRoles: [],
            companies: [],
            projects: [],
            user: {
                name: null,
                email: null,
                customer_positions_enabled: false,
                customer_timeline_enabled: false,
                customer_file_manager_enabled: false,
                customer_documentations_enabled: false,
                customer_boards_enabled: false,
                customer_relationships_enabled: false,
                relationships_contact_visible_to_customers: false,
                relationships_contact_visible_to_employees: false,
                relationships_contact_visible_to_service_providers: false,
                customer_finances_enabled: false,
                customer_server_monitoring_enabled: false,
                customer_vault_enabled: false,
                customer_cms_enabled: false,
                ai_enabled: true,
                salutation: null,
                country: null,
                city: null,
                postal_code: null,
                street: null,
                house_number: null,
                phone_landline: null,
                phone_mobile: null,
                linkedin_url: null,
                xing_url: null,
                instagram_url: null,
                facebook_url: null,
                file_manager_storage_limit_mb: 500,
                role_id: null,
                company_id: null,
                company_ids: [],
                company_assignments: [],
                project_ids: [],
                password_login_enabled: false,
                is_test_account: false,
                status: true,
                password: null,
            },
        }
    },
    mounted() {
        this.getUserRoles();
        this.getCompanies();
        this.getProjects();
    },
    computed: {
        selectedRole() {
            return this.userRoles.find((role) => String(role.id) === String(this.user.role_id)) || null;
        },
        selectedRoleSlug() {
            if (!this.selectedRole) {
                return '';
            }

            return deriveRoleGroup(this.selectedRole);
        },
        isCustomerRoleSelected() {
            return this.selectedRoleSlug === 'customer';
        },
        isAdminRoleSelected() {
            return this.selectedRoleSlug === 'admin';
        },
        isAccountSettingsRoleSelected() {
            if (!this.selectedRole || this.isAdminRoleSelected) {
                return false;
            }

            return ['customer', 'employee', 'service_provider', 'guest'].includes(this.selectedRoleSlug);
        },
        isProjectAssignableRoleSelected() {
            return this.isAccountSettingsRoleSelected;
        },
        showSystemFeatureSettings() {
            return this.isAccountSettingsRoleSelected;
        },
        isGuestRoleSelected() {
            return this.selectedRoleSlug === 'guest';
        },
        isServiceProviderRoleSelected() {
            return this.selectedRoleSlug === 'service_provider';
        },
        isCustomerPositionsFeatureVisible() {
            return isUserSystemFeatureVisible('customer_positions_enabled', this.selectedRoleSlug);
        },
        isCustomerTimelineFeatureVisible() {
            return isUserSystemFeatureVisible('customer_timeline_enabled', this.selectedRoleSlug);
        },
        isCustomerFinancesFeatureVisible() {
            return isUserSystemFeatureVisible('customer_finances_enabled', this.selectedRoleSlug);
        },
        isBoardsFeatureVisible() {
            return isUserSystemFeatureVisible('customer_boards_enabled', this.selectedRoleSlug);
        },
        isRelationshipsFeatureVisible() {
            return isUserSystemFeatureVisible('customer_relationships_enabled', this.selectedRoleSlug);
        },
        isCmsFeatureVisible() {
            return isUserSystemFeatureVisible('customer_cms_enabled', this.selectedRoleSlug);
        },
        showStorageLimitCard() {
            return (this.isCustomerRoleSelected || this.isGuestRoleSelected) && !!this.user.customer_file_manager_enabled;
        },
        storageLimitOptions() {
            return [
                {id: 500, name: '500 MB'},
                {id: 1024, name: '1 GB'},
                {id: 10240, name: '10 GB'},
                {id: 51200, name: '50 GB'},
                {id: null, name: this.$t('Unlimited').toString()},
            ];
        },
    },
    methods: {
        updateSystemFeature({field, value}) {
            if (!Object.prototype.hasOwnProperty.call(this.user, field)) {
                return;
            }

            this.user[field] = value;
        },
        saveUser() {
            const self = this;
            self.loading = true;
            const companyAssignment = normalizeUserCompanyAssignment(
                self.selectedRoleSlug,
                self.user.company_id,
                self.user.company_ids,
                self.companies,
                self.user.company_assignments
            );
            const payload = {
                ...self.user,
                company_id: companyAssignment.companyId,
                company_ids: companyAssignment.companyIds,
                company_assignments: companyAssignment.companyAssignments,
                file_manager_storage_limit_mb: self.resolveStorageLimitPayloadValue(),
                project_ids: self.isProjectAssignableRoleSelected ? self.normalizeProjectIds(self.user.project_ids) : [],
                ai_enabled: !!self.user.ai_enabled,
                customer_positions_enabled: self.isCustomerPositionsFeatureVisible ? (self.isServiceProviderRoleSelected || !!self.user.customer_positions_enabled) : false,
                customer_timeline_enabled: self.isCustomerTimelineFeatureVisible ? (self.isServiceProviderRoleSelected || !!self.user.customer_timeline_enabled) : false,
                customer_finances_enabled: self.isCustomerFinancesFeatureVisible ? (self.isServiceProviderRoleSelected || !!self.user.customer_finances_enabled) : false,
                customer_boards_enabled: self.isBoardsFeatureVisible ? !!self.user.customer_boards_enabled : false,
                customer_relationships_enabled: self.isRelationshipsFeatureVisible ? !!self.user.customer_relationships_enabled : false,
                customer_cms_enabled: self.isCmsFeatureVisible ? !!self.user.customer_cms_enabled : false,
                password_login_enabled: !!self.user.password_login_enabled,
                is_test_account: !!self.user.is_test_account,
            };
            axios.post('api/workspace/admin/users', payload).then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                if (self.embedded) {
                    self.$emit('saved', response.data.user);
                    return;
                }

                self.$router.push('/workspace/admin/users/' + response.data.user.id + '/edit');
            }).catch(function () {
                self.loading = false;
            });
        },
        getUserRoles() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/admin/users/user-roles').then(function (response) {
                self.userRoles = response.data;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        getProjects() {
            const self = this;
            axios.get('api/workspace/admin/users/projects').then(function (response) {
                self.projects = Array.isArray(response.data) ? response.data : [];
            }).catch(function () {
                self.projects = [];
            });
        },
        getCompanies() {
            const self = this;
            axios.get('api/workspace/admin/users/companies').then(function (response) {
                self.companies = Array.isArray(response.data) ? response.data : [];
            }).catch(function () {
                self.companies = [];
            });
        },
        normalizeStorageLimit(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const normalized = Number(value);
            if (!Number.isFinite(normalized) || normalized <= 0) {
                return null;
            }

            const normalizedInt = Math.floor(normalized);
            const allowed = [500, 1024, 10240, 51200];
            if (!allowed.includes(normalizedInt)) {
                return null;
            }

            return normalizedInt;
        },
        normalizeProjectIds(value) {
            if (!Array.isArray(value)) {
                return [];
            }

            return value
                .map((projectId) => Number(projectId))
                .filter((projectId) => Number.isInteger(projectId) && projectId > 0)
                .filter((projectId, index, list) => list.indexOf(projectId) === index);
        },
        resolveStorageLimitPayloadValue() {
            if (this.isAdminRoleSelected || !this.showStorageLimitCard) {
                return null;
            }

            return this.normalizeStorageLimit(this.user.file_manager_storage_limit_mb);
        },
    }
}
</script>

<style scoped>
.user-account-create-embedded {
    height: 100%;
    background: #fff;
    overscroll-behavior: contain;
}

.user-account-create-toolbar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    min-height: 3.25rem;
    align-items: stretch;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.user-account-create-toolbar-title {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    padding: 0 1rem;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.user-account-create-close {
    display: none;
    width: 3.25rem;
    flex: 0 0 3.25rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-right: 1px solid var(--border-ultralight);
    background: transparent;
    color: var(--ui-text-subtle);
}

.user-account-create-close:focus,
.user-account-create-submit:focus {
    outline: none;
}

.user-account-create-submit {
    min-width: 6.5rem;
    padding: 0 1.25rem;
    border: 0;
    background: var(--ui-button-primary-bg, #1f2933);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1rem;
}

.user-account-create-embedded .max-w-7xl {
    max-width: none;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.user-account-create-embedded .mt-6.shadow {
    margin-top: 0;
    border-radius: 0;
    box-shadow: none;
}

.user-account-create-embedded form > .max-w-7xl + .max-w-7xl {
    border-top: 1px solid var(--border-ultralight);
}

.user-account-create-embedded .user-account-create-section {
    display: block;
    padding: 1.5rem 1.75rem;
}

.user-account-create-embedded .user-account-create-section > div + div {
    margin-top: 1.25rem;
}

@media (max-width: 1279px) {
    .user-account-create-close {
        display: inline-flex;
    }
}
</style>
